import { useState } from 'react';
import {
  Banner,
  BannerDescription,
  Button,
  Divider,
  Heading,
  InlineStack,
  Modal,
  ModalBody,
  ModalFooter,
  ModalInset,
  Paragraph,
  TextField,
  Toggle,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { formatNumber, formatNumberAndPluralize } from '@/utils/number';
import { useTrackErrorEncountered } from '../hooks';

export interface PeopleDeleteConfirmModalData {
  deletedPeopleCount: number;
  /** Filter-based select-all — the count is an estimate, and the delete goes by filter. */
  isBulkDeletion: boolean;
  /**
   * Performs the delete (and the caller's row-greying/reload bookkeeping).
   * Rejects on failure so the modal shows the error and stays open.
   */
  onDelete: (options: { suppress: boolean }) => Promise<void>;
}

type SuppressSectionProps = {
  suppress: boolean;
  onToggle: () => void;
  /** "these people" / "this person". */
  theseOrThis: string;
};

function SuppressSection({ suppress, onToggle, theseOrThis }: SuppressSectionProps) {
  return (
    <>
      <InlineStack alignBlock="center" alignInline="space-between" mb="100">
        <Heading level="4" id="delete-and-suppress-label">
          Are you deleting for privacy compliance (GDPR/CCPA)?
        </Heading>
        <Toggle
          data-test-people-delete-confirm-modal-suppress-switch
          ariaLabelledby="delete-and-suppress-label"
          id="delete-and-suppress-toggle"
          isChecked={suppress}
          onChange={onToggle}
          size="sm"
        />
      </InlineStack>
      <Paragraph>
        If you're deleting for privacy compliance, {theseOrThis}'s identifiers will be suppressed to prevent adding them
        again.
      </Paragraph>
      {suppress ? (
        <Banner variant="caution" mt="200">
          <BannerDescription>You won't be able to add {theseOrThis} again</BannerDescription>
        </Banner>
      ) : null}
    </>
  );
}

type DeleteFootnotesProps = {
  suppress: boolean;
  isBulkDeletion: boolean;
  theseOrThis: string;
};

function DeleteFootnotes({ suppress, isBulkDeletion, theseOrThis }: DeleteFootnotesProps) {
  return (
    <>
      {!suppress ? (
        <Paragraph variant="secondary" mt="100">
          *If new profiles are created for {theseOrThis}, they could receive automations they've already triggered.
        </Paragraph>
      ) : null}

      {isBulkDeletion ? (
        <Paragraph data-test-people-delete-confirm-modal-exact-count-alert variant="secondary" mt="100">
          The exact number of deleted people may be different because filter matching is processed at the time of
          deletion.
        </Paragraph>
      ) : null}
    </>
  );
}

/**
 * Type-the-count confirmation for the bulk profile delete, with the
 * privacy-compliance (GDPR/CCPA) suppression toggle. The count is accepted
 * either comma-formatted or bare.
 */
export const PeopleDeleteConfirmModal = ({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<PeopleDeleteConfirmModalData>) => {
  const { deletedPeopleCount, isBulkDeletion, onDelete } = data;
  const snackbars = useSnackbars();
  const trackError = useTrackErrorEncountered();
  const { hasPermission } = usePermissions();

  const [confirmationString, setConfirmationString] = useState('');
  const [suppress, setSuppress] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isPlural = deletedPeopleCount > 1;
  const subjectPhrase = isPlural ? 'These people' : 'This person';
  const theseOrThis = isPlural ? 'these people' : 'this person';

  const title = isPlural ? `Delete ${formatNumber(deletedPeopleCount)} people forever` : 'Delete forever';

  const countConfirmed =
    confirmationString === formatNumber(deletedPeopleCount) || confirmationString === deletedPeopleCount.toString();
  const isSubmitDisabled = isDeleting || !countConfirmed || !hasPermission('journeys/people/delete');

  const handleDelete = () => {
    if (isSubmitDisabled) return;
    setIsDeleting(true);
    void (async () => {
      try {
        await onDelete({ suppress });
        const countedPeople = formatNumberAndPluralize(deletedPeopleCount, 'person');
        snackbars.create({
          variant: 'default',
          id: 'people.delete',
          message: suppress
            ? `${countedPeople} will be deleted and suppressed soon`
            : `${countedPeople} will be deleted soon`,
        });
        onConfirm();
      } catch {
        const message = `${subjectPhrase} couldn't be deleted. Try again or contact support.`;
        snackbars.create({ variant: 'critical', id: 'people.delete.modal', message });
        trackError('People Bulk Delete', message);
        setIsDeleting(false);
      }
    })();
  };

  return (
    <Modal state={modalState} title={title} onClose={onClose} isDismissable={!isDeleting}>
      <ModalBody>
        <Paragraph mb="100">{subjectPhrase} will be deleted immediately.</Paragraph>
        <Paragraph>You can't undo this action.</Paragraph>

        <ModalInset my="200">
          <Divider />
        </ModalInset>

        <SuppressSection
          suppress={suppress}
          onToggle={() => setSuppress((value) => !value)}
          theseOrThis={theseOrThis}
        />

        <ModalInset my="200">
          <Divider />
        </ModalInset>

        <InlineStack alignBlock="center" alignInline="space-between" gap="200">
          <Heading level="4" id="people-delete-count-label" flexShrink="0" whiteSpace="nowrap">
            Confirm how many people you're deleting{suppress ? ' and suppressing' : '*'}
          </Heading>
          <TextField
            data-test-people-delete-confirm-modal-count-input
            id="people-delete-count-input"
            ariaLabelledby="people-delete-count-label"
            name="count"
            placeholder="e.g. 12,567"
            value={confirmationString}
            onInput={(event) => setConfirmationString(event.target.value)}
            flexShrink="1"
          />
        </InlineStack>

        <DeleteFootnotes suppress={suppress} isBulkDeletion={isBulkDeletion} theseOrThis={theseOrThis} />
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" onClick={() => onClose()} isDisabled={isDeleting}>
          Cancel
        </Button>
        <Button
          data-test-people-delete-confirm-modal-submit
          variant="primary"
          isCritical
          isDisabled={isSubmitDisabled}
          onClick={handleDelete}
        >
          Delete {suppress ? 'and suppress' : 'forever'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
